import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon, EditIcon, TrashIcon, ClockIcon, CalendarIcon, HomeIcon, XIcon } from './Icons'
import { PropertyForm } from './PropertyForm'
import { buildApiUrl } from '../utils/api'

export interface Property {
  id: number
  customer_id: number
  property_name: string
  address: string
  property_type: string
  number_of_units: number
  property_value?: number
  purchase_price?: number
  purchase_date?: string
  financed_by_us: boolean
  mortgage_amount?: number
  original_mortgage_amount?: number
  current_interest_rate?: number
  amortization_years?: number
  term_years?: number
  mortgage_start_date?: string
  renewal_date?: string
  lender_name?: string
  mortgage_type?: string
  payment_frequency?: string
  monthly_payment?: number
  bedrooms?: number
  bathrooms?: number
  square_feet?: number
  year_built?: number
  lot_size?: number
  lot_size_unit: string
  is_rental_property: boolean
  monthly_rental_income?: number
  annual_property_taxes?: number
  annual_insurance?: number
  monthly_maintenance_estimate?: number
  notes?: string
  additional_features?: object
  status: string
  auto_renewal_reminders: boolean
  renewal_reminder_months: number
  auto_rate_review_reminders: boolean
  rate_review_frequency_months: number
  created_at: string
  updated_at: string
  customer_name?: string
  customer_email?: string
  first_name?: string
  last_name?: string
}

interface PropertyManagementProps {
  customerId: number
  customerName: string
  onCreateTask?: (propertyId: number, taskType: string, description: string, dueDate: string) => void
}

interface RenewalInfo {
  property: Property
  daysUntilRenewal: number | null
  renewalStatus: 'unknown' | 'overdue' | 'urgent' | 'upcoming' | 'planning' | 'future'
  suggestedTasks: Array<{
    type: string
    description: string
    suggested_due_date: string
    priority: string
  }>
}

export function PropertyManagement({ customerId, customerName, onCreateTask }: PropertyManagementProps) {
  const { t } = useTranslation()
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingProperty, setEditingProperty] = useState<Property | null>(null)
  const [activeTab, setActiveTab] = useState<'all' | 'financed' | 'rental'>('all')
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')
  const [renewalInfo, setRenewalInfo] = useState<{[key: number]: RenewalInfo}>({})

  useEffect(() => {
    fetchProperties()
  }, [customerId])

  const fetchProperties = async () => {
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) throw new Error('Authentication required')

      const response = await fetch(buildApiUrl(`api/properties?customer_id=${customerId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const responseText = await response.text()
        console.error('Properties API Error:', {
          status: response.status,
          statusText: response.statusText,
          body: responseText
        })
        throw new Error(`Failed to fetch properties: ${response.status} ${response.statusText}`)
      }

      const responseText = await response.text()
      let data
      try {
        data = JSON.parse(responseText)
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError)
        console.error('Response was:', responseText)
        throw new Error('Server returned invalid JSON. This may indicate a server error.')
      }
      setProperties(data.properties || [])

      // Fetch renewal info for properties with renewal dates
      const propertiesWithRenewal = data.properties.filter((p: Property) => p.renewal_date)
      for (const property of propertiesWithRenewal) {
        try {
          const renewalResponse = await fetch(buildApiUrl(`api/properties/${property.id}/renewal-info`), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
          if (renewalResponse.ok) {
            const renewalData = await renewalResponse.json()
            setRenewalInfo(prev => ({
              ...prev,
              [property.id]: renewalData.renewalInfo
            }))
          }
        } catch (error) {
          console.error('Failed to fetch renewal info for property:', property.id, error)
        }
      }
    } catch (err) {
      console.error('Error fetching properties:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch properties')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProperty = async (propertyId: number) => {
    if (!confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('Authentication required')

      const response = await fetch(buildApiUrl(`api/properties/${propertyId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to delete property')
      }

      await fetchProperties()
    } catch (err) {
      console.error('Error deleting property:', err)
      alert('Failed to delete property. Please try again.')
    }
  }

  const handleCreateTask = async (propertyId: number, taskType: string, description: string, dueDate: string) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('Authentication required')

      const response = await fetch(buildApiUrl(`api/properties/${propertyId}/create-task`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          task_type: taskType,
          description: description,
          due_date: dueDate,
          priority: 'medium'
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create task')
      }

      if (onCreateTask) {
        onCreateTask(propertyId, taskType, description, dueDate)
      }

      alert('Task created successfully!')
    } catch (err) {
      console.error('Error creating task:', err)
      alert('Failed to create task. Please try again.')
    }
  }

  const formatCurrency = (amount: number | undefined | null) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatPercentage = (rate: number | undefined | null) => {
    if (!rate) return 'N/A'
    return `${rate.toFixed(2)}%`
  }

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-CA')
  }

  const getRenewalStatusColor = (status: string) => {
    switch (status) {
      case 'overdue': return 'text-red-600 bg-red-50 border-red-200'
      case 'urgent': return 'text-orange-600 bg-orange-50 border-orange-200'
      case 'upcoming': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'planning': return 'text-gray-900 bg-blue-50 border-blue-200'
      default: return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getFilteredProperties = () => {
    switch (activeTab) {
      case 'financed':
        return properties.filter(p => p.financed_by_us)
      case 'rental':
        return properties.filter(p => p.is_rental_property)
      default:
        return properties
    }
  }

  const filteredProperties = getFilteredProperties()

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading properties...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error Loading Properties</h3>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchProperties}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <HomeIcon className="h-8 w-8 text-gray-900 dark:text-gray-600 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('customers.properties.title')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('customers.properties.subtitle', { customerName })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'cards' 
                    ? 'bg-gray-900 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="grid grid-cols-2 gap-1 w-4 h-4">
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                  <div className="bg-current rounded-sm"></div>
                </div>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="flex flex-col space-y-1 w-4 h-4">
                  <div className="bg-current h-0.5 rounded"></div>
                  <div className="bg-current h-0.5 rounded"></div>
                  <div className="bg-current h-0.5 rounded"></div>
                </div>
              </button>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              <PlusIcon size={16} />
              <span>{t('customers.properties.addProperty')}</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-6 mt-4">
          {[
            { id: 'all', label: t('customers.properties.tabs.allProperties'), count: properties.length },
            { id: 'financed', label: t('customers.properties.tabs.financedByUs'), count: properties.filter(p => p.financed_by_us).length },
            { id: 'rental', label: t('customers.properties.tabs.rentalProperties'), count: properties.filter(p => p.is_rental_property).length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id 
                  ? 'bg-blue-700 text-blue-100' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {filteredProperties.length === 0 ? (
          <div className="text-center py-12">
            <HomeIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('customers.properties.emptyState.noPropertiesFound')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {activeTab === 'all' 
                ? t('customers.properties.emptyState.startBuilding')
                : t('customers.properties.emptyState.noPropertiesForTab', { tab: activeTab })
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              {t('customers.properties.addFirstProperty')}
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredProperties.map((property) => (
              <PropertyCard 
                key={property.id} 
                property={property}
                renewalInfo={renewalInfo[property.id]}
                onEdit={() => setEditingProperty(property)}
                onDelete={() => handleDeleteProperty(property.id)}
                onCreateTask={handleCreateTask}
                formatCurrency={formatCurrency}
                formatPercentage={formatPercentage}
                formatDate={formatDate}
                getRenewalStatusColor={getRenewalStatusColor}
              />
            ))}
          </div>
        ) : (
          <PropertyTable 
            properties={filteredProperties}
            renewalInfo={renewalInfo}
            onEdit={setEditingProperty}
            onDelete={handleDeleteProperty}
            onCreateTask={handleCreateTask}
            formatCurrency={formatCurrency}
            formatPercentage={formatPercentage}
            formatDate={formatDate}
            getRenewalStatusColor={getRenewalStatusColor}
          />
        )}
      </div>

      {/* Property Form Modal */}
      {(showCreateModal || editingProperty) && (
        <PropertyForm
          property={editingProperty}
          customerId={customerId}
          onClose={() => {
            setShowCreateModal(false)
            setEditingProperty(null)
          }}
          onSave={() => {
            fetchProperties()
            setShowCreateModal(false)
            setEditingProperty(null)
          }}
        />
      )}
    </div>
  )
}

// Property Card Component
interface PropertyCardProps {
  property: Property
  renewalInfo?: RenewalInfo
  onEdit: () => void
  onDelete: () => void
  onCreateTask: (propertyId: number, taskType: string, description: string, dueDate: string) => void
  formatCurrency: (amount: number | undefined | null) => string
  formatPercentage: (rate: number | undefined | null) => string
  formatDate: (dateString: string | undefined | null) => string
  getRenewalStatusColor: (status: string) => string
}

function PropertyCard({ 
  property, 
  renewalInfo, 
  onEdit, 
  onDelete, 
  onCreateTask, 
  formatCurrency, 
  formatPercentage, 
  formatDate, 
  getRenewalStatusColor 
}: PropertyCardProps) {
  const [showTaskMenu, setShowTaskMenu] = useState(false)

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 hover:shadow-md transition-shadow">
      {/* Property Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {property.property_name}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {property.address}
          </p>
          <div className="flex items-center space-x-2">
            {property.financed_by_us && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
                Our Mortgage
              </span>
            )}
            {property.is_rental_property && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                Rental
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onEdit}
            className="p-1 text-gray-400 hover:text-gray-900 dark:hover:text-gray-600 transition-colors"
          >
            <EditIcon size={16} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <TrashIcon size={16} />
          </button>
        </div>
      </div>

      {/* Property Details */}
      <div className="space-y-3">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Type:</span>
            <p className="text-gray-900 dark:text-gray-100 capitalize">{property.property_type.replace('_', ' ')}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Units:</span>
            <p className="text-gray-900 dark:text-gray-100">{property.number_of_units}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Value:</span>
            <p className="text-gray-900 dark:text-gray-100">{formatCurrency(property.property_value)}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Mortgage:</span>
            <p className="text-gray-900 dark:text-gray-100">{formatCurrency(property.mortgage_amount)}</p>
          </div>
        </div>

        {/* Mortgage Info */}
        {property.financed_by_us && property.renewal_date && (
          <div className={`p-3 rounded-md border ${getRenewalStatusColor(renewalInfo?.renewalStatus || 'unknown')}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Mortgage Renewal</span>
                                {renewalInfo?.daysUntilRenewal !== null && renewalInfo?.daysUntilRenewal !== undefined && (
                <span className="text-xs">
                  {renewalInfo.daysUntilRenewal > 0 
                    ? `${renewalInfo.daysUntilRenewal} days` 
                    : `${Math.abs(renewalInfo.daysUntilRenewal)} days overdue`
                  }
                </span>
              )}
            </div>
            <div className="text-sm space-y-1">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Renewal Date:</span>
                <span className="ml-1">{formatDate(property.renewal_date)}</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Current Rate:</span>
                <span className="ml-1">{formatPercentage(property.current_interest_rate)}</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Lender:</span>
                <span className="ml-1">{property.lender_name || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-600">
          <div className="relative">
            <button
              onClick={() => setShowTaskMenu(!showTaskMenu)}
              className="flex items-center space-x-2 px-3 py-1 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              <CalendarIcon size={14} />
              <span>Create Task</span>
            </button>
            
            {showTaskMenu && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="py-1">
                  <button
                    onClick={() => {
                      const dueDate = new Date()
                      dueDate.setDate(dueDate.getDate() + 7)
                      onCreateTask(property.id, 'rate_review', 'Review current market rates', dueDate.toISOString().split('T')[0])
                      setShowTaskMenu(false)
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Rate Review
                  </button>
                  <button
                    onClick={() => {
                      const dueDate = new Date()
                      dueDate.setDate(dueDate.getDate() + 3)
                      onCreateTask(property.id, 'contact_client', 'Contact client about property', dueDate.toISOString().split('T')[0])
                      setShowTaskMenu(false)
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Contact Client
                  </button>
                  <button
                    onClick={() => {
                      const dueDate = new Date()
                      dueDate.setDate(dueDate.getDate() + 14)
                      onCreateTask(property.id, 'property_review', 'Annual property review', dueDate.toISOString().split('T')[0])
                      setShowTaskMenu(false)
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Property Review
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {property.is_rental_property && property.monthly_rental_income && (
            <div className="text-right text-sm">
              <span className="text-gray-500 dark:text-gray-400">Monthly Income:</span>
              <p className="text-green-600 dark:text-green-400 font-medium">
                {formatCurrency(property.monthly_rental_income)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Property Table Component (placeholder for table view)
function PropertyTable({ properties, renewalInfo, onEdit, onDelete, onCreateTask, formatCurrency, formatPercentage, formatDate, getRenewalStatusColor }: any) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Property
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Value
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Mortgage
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Renewal
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {properties.map((property: Property) => (
            <tr key={property.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {property.property_name}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {property.address}
                  </div>
                  <div className="flex space-x-2 mt-1">
                    {property.financed_by_us && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        Our Mortgage
                      </span>
                    )}
                    {property.is_rental_property && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                        Rental
                      </span>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 capitalize">
                {property.property_type.replace('_', ' ')}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                {formatCurrency(property.property_value)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {formatCurrency(property.mortgage_amount)}
                </div>
                {property.current_interest_rate && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatPercentage(property.current_interest_rate)}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {property.renewal_date ? (
                  <div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(property.renewal_date)}
                    </div>
                    {renewalInfo[property.id] && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRenewalStatusColor(renewalInfo[property.id].renewalStatus)}`}>
                        {renewalInfo[property.id].renewalStatus}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400">N/A</span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(property)}
                    className="text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    <EditIcon size={16} />
                  </button>
                  <button
                    onClick={() => onDelete(property.id)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}


